import { isEmpty } from 'utils/ObjectUtil';

import { shotTypes } from 'utils/golf/GolfShotMapUtil';

import {
    FETCH_GOLF_PLAYER_SHOTS,
    FETCH_GOLF_PLAYER_SHOTS_FETCHING,
    FETCH_GOLF_PLAYER_SHOTS_SUCCESS,
    FETCH_GOLF_PLAYER_SHOTS_FAIL,
    UPDATE_GOLF_PLAYER_SHOTS,
    UPDATE_GOLF_PLAYER_SHOTS_UPDATING,
    UPDATE_GOLF_PLAYER_SHOTS_SUCCESS,
    UPDATE_GOLF_PLAYER_SHOTS_FAIL
} from 'actions/golfShotsActionTypes';

const INITIAL_STATE = {
    playerShots: {},
    playerShotsFetching: false,
    playerShotsFetchSuccess: false,
    playerShotsFetchFail: false,
    playerShotsFetchFailStatus: undefined,
    playerShotsUpdating: false,
    playerShotsUpdateSuccess: false,
    playerShotsUpdateFail: false,
    playerShotsUpdateFailReason06: false
};

// ======= Fetching Handlers =======
const playerShotsFetching = (state) => {
    return {
        ...state,
        playerShotsFetching: true,
        playerShotsFetchSuccess: false,
        playerShotsFetchFail: false,
        playerShotsUpdateFailReason06: false
    };
};

const playerShotsFetchFinished = (success) => (state, action) => {
    return {
        ...state,
        playerShots: success
            ? {
                  ...state.playerShots,
                  [action.scorecardId]: action.playerShots
              }
            : state.playerShots,
        playerShotsFetching: false,
        playerShotsFetchSuccess: success,
        playerShotsFetchFail: !success,
        playerShotsUpdateFailReason06: false,
        playerShotsFetchFailStatus: action.statusCode
    };
};

const playerShotsUpdating = (state) => {
    return {
        ...state,
        playerShotsUpdating: true,
        playerShotsUpdateSuccess: false,
        playerShotsUpdateFail: false,
        playerShotsUpdateFailReason06: false
    };
};

const playerShotsUpdateFinished = (success) => (state, action) => {
    let updatedScorecardPlayerShots = {};

    if (success) {
        const scorecardPlayerShots = state.playerShots[action.scorecardId] || {};
        const updatedHoleShots = scorecardPlayerShots.holeShots.map((holeShots) => {
            if (holeShots.holeNumber === action.holeNumber) {
                return {
                    ...holeShots,
                    shots: action.shots
                };
            }
            return holeShots;
        });
        updatedScorecardPlayerShots = {
            ...scorecardPlayerShots,
            holeShots: updatedHoleShots
        };
    }

    return {
        ...state,
        playerShots: success
            ? {
                  ...state.playerShots,
                  [action.scorecardId]: updatedScorecardPlayerShots
              }
            : state.playerShots,
        playerShotsUpdating: false,
        playerShotsUpdateSuccess: success,
        playerShotsUpdateFail: !success,
        playerShotsUpdateFailReason06: action.reasonCode === '06' // occurs when shot is added to the green
    };
};

// ======= Handler Assignments =======
const handlers = {
    [FETCH_GOLF_PLAYER_SHOTS_FETCHING]: playerShotsFetching,
    [FETCH_GOLF_PLAYER_SHOTS_SUCCESS]: playerShotsFetchFinished(true),
    [FETCH_GOLF_PLAYER_SHOTS_FAIL]: playerShotsFetchFinished(false),
    [UPDATE_GOLF_PLAYER_SHOTS_UPDATING]: playerShotsUpdating,
    [UPDATE_GOLF_PLAYER_SHOTS_SUCCESS]: playerShotsUpdateFinished(true),
    [UPDATE_GOLF_PLAYER_SHOTS_FAIL]: playerShotsUpdateFinished(false)
};

// ======= Default Action =======
export default function golfPlayerShots(state = INITIAL_STATE, action = {}) {
    const handler = handlers[action.type];

    if (!handler) return state;
    return handler(state, action);
}

// ======= Selectors =======
export const getPlayerShots = (state, scorecardId) => {
    return state.golfPlayerShots.playerShots[scorecardId] || {};
};

export const getPlayerShotsForHole = (state, scorecardId, holeNumber) => {
    const playerShots = state.golfPlayerShots.playerShots[scorecardId] || {};
    return isEmpty(playerShots)
        ? {}
        : playerShots.holeShots.find((holeShot) => holeShot.holeNumber === parseInt(holeNumber, 10));
};

// just the shots (no putts)
export const getPlayerHoleShots = (state, scorecardId, holeNumber) => {
    // NO shots AFTER the first putt
    // Can have other shots after the first putt, ignore those
    const playerShots = state.golfPlayerShots.playerShots[scorecardId] || {};
    if (isEmpty(playerShots)) {
        return [];
    }
    const holeShots = playerShots.holeShots.find((holeShot) => holeShot.holeNumber === parseInt(holeNumber, 10));
    let shots = [];
    for (let i = 0; i < holeShots.shots.length; i++) {
        const shot = holeShots.shots[i];
        if (shot.shotType === shotTypes.putt) {
            break;
        }
        shots.push(shot);
    }
    return shots;
};

// just the putts
export const getPlayerHolePuttShots = (state, scorecardId, holeNumber) => {
    const playerShots = state.golfPlayerShots.playerShots[scorecardId] || {};
    if (isEmpty(playerShots)) {
        return [];
    }
    const holeShots = playerShots.holeShots.find((holeShot) => holeShot.holeNumber === parseInt(holeNumber, 10));
    let shots = [];
    for (let i = 0; i < holeShots.shots.length; i++) {
        const shot = holeShots.shots[i];
        if (shot.shotType === shotTypes.putt) {
            shots.push(shot);
        }
    }
    return shots;
};

export const getPinPositionForHole = (state, scorecardId, holeNumber) => {
    return getPlayerShotsForHole(state, scorecardId, holeNumber).pinPosition;
};

export const getHoleImageUrl = (state, scorecardId, holeNumber) => {
    return getPlayerShotsForHole(state, scorecardId, holeNumber).holeImageUrl;
};

export const isPlayerShotsFetching = (state) => {
    return state.golfPlayerShots.playerShotsFetching;
};

export const didPlayerShotsFetchFail = (state) => {
    return state.golfPlayerShots.playerShotsFetchFail;
};

export const didPlayerShotsFetchSucceed = (state) => {
    return state.golfPlayerShots.playerShotsFetchSuccess;
};

export const isPlayerShotsUpdating = (state) => {
    return state.golfPlayerShots.playerShotsUpdating;
};

export const didPlayerShotsUpdateFail = (state) => {
    return state.golfPlayerShots.playerShotsUpdateFail;
};

export const didPlayerShotsUpdateSucceed = (state) => {
    return state.golfPlayerShots.playerShotsUpdateSuccess;
};

export const didPlayerShotsUpdateFailReason06 = (state) => {
    return state.golfPlayerShots.playerShotsUpdateFailReason06;
};

export const getPlayerShotsFetchFailStatus = (state) => {
    return state.golfPlayerShots.playerShotsFetchFailStatus;
};

// ======= Actions =======

export function fetchPlayerShots(scorecardId) {
    return {
        type: FETCH_GOLF_PLAYER_SHOTS,
        scorecardId: scorecardId
    };
}

export function updatePlayerShots(scorecardId, holeNumber, shots) {
    return {
        type: UPDATE_GOLF_PLAYER_SHOTS,
        scorecardId: scorecardId,
        holeNumber: holeNumber,
        shots: shots
    };
}
